import { useState, useEffect } from 'react';
import HTTP from 'services/http';

interface ILastOrderForm {
  documentId: number;
  fileName: string;
  completedDate: string;
  fillCycleNumber: number;
  orderId: number;
}

interface IUseLastOrderFormResult {
  orderForm: ILastOrderForm | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Hook to fetch the last completed order form for a specific therapy
 * @param patientId - Patient ID
 * @param therapyId - Therapy ID
 * @param enabled - Whether to fetch the data (default: true)
 */
export const useLastOrderForm = (
  patientId: number | undefined,
  therapyId: number | undefined,
  enabled = true,
): IUseLastOrderFormResult => {
  const [orderForm, setOrderForm] = useState<ILastOrderForm | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refetchTrigger, setRefetchTrigger] = useState(0);

  const refetch = () => {
    setRefetchTrigger(prev => prev + 1);
  };

  useEffect(() => {
    if (!enabled || !patientId || !therapyId) {
      return;
    }

    const fetchLastOrderForm = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await HTTP.get(
          `/patients/${patientId}/therapies/${therapyId}/last-order-form`,
        );

        if (response.data) {
          setOrderForm(response.data);
        } else {
          setOrderForm(null);
        }
      } catch (err: any) {
        console.error('Error fetching last order form:', err);
        setError(err?.response?.data?.message || 'Failed to fetch last order form');
        setOrderForm(null);
      } finally {
        setLoading(false);
      }
    };

    fetchLastOrderForm();
  }, [patientId, therapyId, enabled, refetchTrigger]);

  return { orderForm, loading, error, refetch };
};
